const STORAGE_PREFIX = "shuttlescore:profilePhoto:";
/** Offline data-URL budget (localStorage). Phone camera JPEGs are compressed to fit. */
const MAX_BYTES = 350_000;
const MAX_EDGE_PX = 512;
const MIN_JPEG_QUALITY = 0.45;

function storageKey(playerId) {
  return `${STORAGE_PREFIX}${playerId}`;
}

/**
 * @param {string} playerId
 * @returns {boolean}
 */
export function hasLocalProfilePhoto(playerId) {
  if (!playerId || typeof localStorage === "undefined") return false;
  try {
    return Boolean(localStorage.getItem(storageKey(playerId)));
  } catch {
    return false;
  }
}

/**
 * @param {string} playerId
 * @returns {string | null} data URL
 */
export function getLocalProfilePhotoDataUrl(playerId) {
  if (!playerId || typeof localStorage === "undefined") return null;
  try {
    return localStorage.getItem(storageKey(playerId));
  } catch {
    return null;
  }
}

/**
 * @param {string} playerId
 * @param {string} dataUrl
 */
export function saveLocalProfilePhoto(playerId, dataUrl) {
  if (!playerId) throw new Error("playerId required");
  if (!dataUrl || !dataUrl.startsWith("data:image/")) {
    throw new Error("Invalid image data");
  }
  if (dataUrl.length > MAX_BYTES) {
    throw new Error("Image is too large for offline storage");
  }
  try {
    localStorage.setItem(storageKey(playerId), dataUrl);
  } catch (e) {
    throw new Error("Could not save photo offline");
  }
}

/**
 * @param {string} playerId
 */
export function removeLocalProfilePhoto(playerId) {
  if (!playerId || typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(storageKey(playerId));
  } catch {
    /* ignore */
  }
}

/**
 * @param {File} file
 * @returns {Promise<string>}
 */
export function readImageFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith("image/")) {
      reject(new Error("Please choose an image file"));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") resolve(reader.result);
      else reject(new Error("Could not read image"));
    };
    reader.onerror = () => reject(new Error("Could not read image"));
    reader.readAsDataURL(file);
  });
}

/**
 * @param {File} file
 * @returns {Promise<HTMLImageElement>}
 */
function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read image"));
    };
    img.src = url;
  });
}

/**
 * Resize + JPEG-compress a photo so it fits offline localStorage.
 * @param {File} file
 * @param {{ maxBytes?: number, maxEdge?: number }} [opts]
 * @returns {Promise<string>} data URL
 */
export async function compressImageFileForProfilePhoto(file, opts = {}) {
  if (!file || !file.type.startsWith("image/")) {
    throw new Error("Please choose an image file");
  }
  const maxBytes = opts.maxBytes ?? MAX_BYTES;
  const startEdge = opts.maxEdge ?? MAX_EDGE_PX;

  const img = await loadImageFromFile(file);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not process image");

  let edge = startEdge;
  let best = "";

  while (edge >= 160) {
    const scale = Math.min(1, edge / Math.max(img.width, img.height, 1));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    canvas.width = w;
    canvas.height = h;
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);

    for (let q = 0.85; q >= MIN_JPEG_QUALITY; q -= 0.1) {
      const dataUrl = canvas.toDataURL("image/jpeg", q);
      if (dataUrl.length <= maxBytes) return dataUrl;
      if (!best || dataUrl.length < best.length) best = dataUrl;
    }
    edge = Math.floor(edge * 0.75);
  }

  if (best && best.length <= maxBytes) return best;
  throw new Error("Image is still too large after compression — try a smaller photo");
}

export { MAX_BYTES as LOCAL_PROFILE_PHOTO_MAX_BYTES, MAX_EDGE_PX as LOCAL_PROFILE_PHOTO_MAX_EDGE };
